import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { Language, Direction, LANGUAGES, DEFAULT_LANGUAGE, Translations } from './types';
import { translations } from './translations';

const LANGUAGE_STORAGE_KEY = 'alice-language';

interface LanguageContextType {
  language: Language;
  direction: Direction;
  setLanguage: (lang: Language) => void;
  t: Translations;
  isRTL: boolean;
  locale: string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored && (stored === 'he' || stored === 'en' || stored === 'ar')) {
        return stored as Language;
      }
    }
    return DEFAULT_LANGUAGE;
  });

  const config = LANGUAGES[language];
  const direction = config.direction;
  const isRTL = direction === 'rtl';
  const locale = config.locale;
  const t = translations[language];

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  }, []);

  // Update document attributes when language changes
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = direction;
    
    // Update meta tags for SEO
    const htmlTag = document.documentElement;
    htmlTag.setAttribute('lang', language);
    htmlTag.setAttribute('dir', direction);
  }, [language, direction]);

  return (
    <LanguageContext.Provider value={{ language, direction, setLanguage, t, isRTL, locale }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Helper hook for getting translated text with fallback
export function useTranslation() {
  const { t, language } = useLanguage();
  
  // Helper to get nested translation key
  const getText = useCallback((section: keyof Translations, key: string): string => {
    const sectionTranslations = t[section] as Record<string, string>;
    if (sectionTranslations && sectionTranslations[key]) {
      return sectionTranslations[key];
    }
    // Fallback to Hebrew
    const heTranslations = translations.he[section] as Record<string, string>;
    return heTranslations?.[key] || key;
  }, [t]);
  
  return { t, language, getText };
}
